/**
 * Die LESBARE Kennung einer Bestellung: Bestellnummer, sonst `id` (Spezifikation § 3.7) - fuer URLs und
 * ueberall, wo die Bestellung eindeutig benannt werden muss. Das ist die eine Ableitung; `App.tsx` und rund
 * ein Dutzend weitere Stellen importieren sie.
 *
 * Die BEZEICHNUNG in einer Meldung ist etwas anderes (`bestellbezeichnung`): erst `orderNo`, dann `name`, dann
 * `id` - ein Name hilft dem Nutzer mehr als eine UUID. Meldungen, die nach einem Drawer-Wechsel erscheinen,
 * benennen die Bestellung darueber (`mitBestellbezug`: "Bestellung <Bezeichnung>: <Satz>"). Das tun `useDrawerTasks`,
 * `useDrawerZahlungsplan`, `useDrawerSpedition` und `useDrawerZahlungen`; `useDrawerBestellfelder` nimmt dieselbe
 * Bezeichnung in seiner eigenen Satzform ("Die Bestellung <Bezeichnung> ..."). Nicht zu `kennungVon` zusammenlegen:
 * der Rueckfall ist ein anderer.
 *
 * Weitere Varianten der Form `orderNo || name || ...` in Anzeige-Komponenten (SammelsendungDrawer, SammelsendungTrennen,
 * DsvGruppenVergleich, BestellgruppenBereich, Verteilung, AufgabenDetail u. a.) sind Beschriftungen, keine Meldungen,
 * und bleiben eigene Ausdruecke - teils mit festem Wort wie "Bestellung" als Rueckfall statt der `id`.
 */
import type { Order } from "./types.ts";

export function kennungVon(bestellung: Order, bestellungId: string): string {
  return String(bestellung.orderNo ?? "") || bestellungId;
}

/**
 * Die Bestellung, wie sie im Bestand steht - oder `undefined`, wenn sie dort (noch/nicht mehr) fehlt.
 * Der Index-Eintrag ist noetig, damit `Order` passt: `Order` fuehrt `orderNo`/`name` nur ueber seinen eigenen
 * Index-Eintrag, und ein Typ, dessen Eigenschaften alle optional sind, weist sonst alles ab, das keine davon benennt
 * (TS-Schwachtypenpruefung, siehe `Order.warehouse`).
 */
type BestellungFuerBezug = { orderNo?: unknown; name?: unknown; [feld: string]: unknown } | undefined;

/** `orderNo`, sonst `name`, sonst die `id`: so nennt eine Meldung die Bestellung. */
export function bestellbezeichnung(bestellung: BestellungFuerBezug, bestellungId: string): string {
  return String(bestellung?.orderNo || bestellung?.name || bestellungId);
}

/**
 * Der eine Eintrag, wenn nach einem gescheiterten Schreiben (409/404) das Nachladen `null` liefert: die Bestellung gibt es
 * nicht mehr (404 der Hub-Zeile bzw. JTL). Nichts wurde geladen und nichts eingespielt, also steht kein Satz ueber "den
 * aktuellen Stand" da und kein "erneut versuchen" (das fuehrte in den naechsten 404). Dieselbe Regel in `useDrawerZahlungen`,
 * `useDrawerTasks` und `useDrawerZahlungsplan` (Fehleranzeige Paket 2, Rulings R-T11d und R-T11f).
 */
export const BESTELLUNG_WEG_MELDUNG = "Die Bestellung gibt es nicht mehr. Bitte neu laden.";

/** "Bestellung <Bezeichnung>: <Satz>" - fuer Meldungen, die der Nutzer nicht mehr in der Bestellung liest, die sie meint. */
export function mitBestellbezug(satz: string, bestellung: BestellungFuerBezug, bestellungId: string): string {
  return `Bestellung ${bestellbezeichnung(bestellung, bestellungId)}: ${satz}`;
}
